import { TagChip } from '@entur/chip';
import { Pagination } from '@entur/menu';
import { Label } from '@entur/typography';
import classNames from 'classnames/bind';
import dayjs from 'dayjs';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { DirectionType } from 'types';
import { LegacyColumns } from 'components/Table/types';
import styles from './styles.module.scss';
import DownloadCsv from 'components/DownloadCsv';
import { Switch } from '@entur/form';

export interface SortAndPaginationProps<T extends { id?: number } = { id?: number }> {
  children: React.ReactNode;
  totalElements?: number;
  page?: number;
  size?: number;
  items?: T[];
  sorting?: string[];
  resultsPerPageOptions?: number[];
  handlePaging: (page: number, size: number) => void;
  handleSorting?: (column: string, direction?: DirectionType) => void;
  renderBottomPagination?: boolean;
  className?: string;
  dataUpdatedAt?: number;
  columns?: LegacyColumns;
  csv?: boolean;
  TAccount?: boolean;
}

/** Pagination, sorting chips, CSV download and T-account toggle around a table. */
export const SortAndPagination = <T extends { id?: number } = { id?: number }>({
  children,
  totalElements = 0,
  page = 0,
  size = 100,
  items = [],
  sorting = [],
  resultsPerPageOptions = [10, 20, 100, 250],
  handlePaging,
  handleSorting = () => {},
  renderBottomPagination = true,
  className,
  dataUpdatedAt,
  columns,
  csv = false,
  TAccount = false,
}: SortAndPaginationProps<T>) => {
  const intl = useIntl();
  const cx = classNames.bind(styles);

  const Taccount_VIEW_KEY = 'isTAccountViewEnabled';

  const [isTAccountViewEnabled, setIsTAccountViewEnabled] = useState(() => {
    try {
      const stored = window.localStorage.getItem(Taccount_VIEW_KEY);
      return stored ? JSON.parse(stored) : false;
    } catch {
      return false;
    }
  });

  const handleTAccountViewChange = (checked: boolean) => {
    setIsTAccountViewEnabled(checked);
    window.localStorage.setItem(Taccount_VIEW_KEY, JSON.stringify(checked));
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('isTAccountViewEnabledChanged', { detail: { enabled: checked } }));
  };

  const renderPagination = () => {
    if (totalElements === 0 || !items.length) {
      return null;
    }
    return (
      <div className={styles.paginationWrapper}>
        <Pagination
          pageCount={Math.ceil(totalElements / Number(size))}
          currentPage={page + 1}
          onPageChange={(requestedPage) => handlePaging(requestedPage - 1, size)}
          resultsPerPage={Number(size)}
          numberOfResults={totalElements}
          onResultsPerPageChange={(pageSize) => handlePaging(0, pageSize)}
          resultsPerPageOptions={resultsPerPageOptions}
          showInput
          className={styles.pagination}
        />
        {csv && columns && (
          <div className={styles.csvWrapper}>
            <DownloadCsv columns={columns} data={items} />
          </div>
        )}
        {TAccount && renderTAccountSwitch()}
      </div>
    );
  };

  const renderSortingChips = () => {
    if (!sorting.length || !items.length) {
      return null;
    }
    return (
      <div>
        <Label>{intl.formatMessage({ id: 'generalValues.sortedBy' })}</Label>
        <div className={styles.chipWrapper}>
          {sorting?.map((sortedColumn) => (
            <TagChip
              key={sortedColumn}
              onClose={() => {
                handleSorting(sortedColumn, undefined);
              }}
            >
              {intl.formatMessage({ id: `chips.${sortedColumn}`, defaultMessage: sortedColumn })}
            </TagChip>
          ))}
        </div>
      </div>
    );
  };

  const renderSortingChipsAndLastUpdated = () => {
    return (
      <div className={styles.sortingAndLastUpdated}>
        {renderSortingChips()}
        {Boolean(dataUpdatedAt) && (
          <span className={styles.lastUpdated}>
            {intl.formatMessage(
              { id: 'generalValues.lastUpdatedAt' },
              { date: dayjs(dataUpdatedAt).format('DD.MM.YYYY HH:mm:ss') }
            )}
          </span>
        )}
      </div>
    );
  };

  const renderTAccountSwitch = () => {
    return (
      <Switch
        checked={isTAccountViewEnabled}
        onChange={() => handleTAccountViewChange(!isTAccountViewEnabled)}
        className={styles.csvWrapper}
      >
        T-konto
      </Switch>
    );
  };

  return (
    <div className={cx(styles.wrapper, className)}>
      {renderSortingChipsAndLastUpdated()}
      {renderPagination()}
      {children}
      {renderBottomPagination && renderPagination()}
    </div>
  );
};
